import { Engine } from '@babylonjs/core/Engines/engine';
import type { Scene } from '@babylonjs/core/scene';
import { AdSlotBinder } from '../advertising/AdSlotBinder';
import { HOUSE_CAMPAIGNS, ManifestAdProvider } from '../advertising/AdProvider';
import { AdReporter } from '../advertising/AdReporter';
import { adBackendUrl, adImageOrigins } from '../config/advertising.config';
import { LEADERBOARD_CONFIG, leaderboardBackendUrl, leaderboardLoginUrl } from '../config/leaderboard.config';
import { LeaderboardService } from '../services/LeaderboardService';
import { formatNumber } from '../ui/dom';
import { AdSurfacePresenter } from '../advertising/AdSurfacePresenter';
import { AssetLoader } from '../assets/AssetLoader';
import { AssetRegistry } from '../assets/AssetRegistry';
import { ASSET_MANIFEST } from '../assets/manifest/assetManifest';
import { AudioManager, type EngineSound } from '../audio/AudioManager';
import { PlayerState } from '../player/PlayerState';
import { SynthSoundBackend } from '../audio/SynthSoundBackend';
import { AUDIO_CONFIG } from '../config/audio.config';
import { RunnerHeightTrail } from '../camera/RunnerHeightTrail';
import { ThirdPersonCamera, type CameraFollowInput } from '../camera/ThirdPersonCamera';
import { CHARACTERS, CharacterCatalog } from '../characters/CharacterCatalog';
import { characterChoices, perksOf, resolveCharacterId, stepCharacter } from '../characters/characterSelection';
import type { CharacterDefinition } from '../characters/CharacterTypes';
import { CollectibleCatalog, DEFAULT_COLLECTIBLES } from '../collectibles/CollectibleCatalog';
import { CollectibleMagnet } from '../collectibles/CollectibleMagnet';
import { TurboBoost } from '../collectibles/TurboBoost';
import { CollectibleManager } from '../collectibles/CollectibleManager';
import { CollectiblePresenter } from '../collectibles/CollectiblePresenter';
import { GoldenShinePresenter } from '../collectibles/GoldenShinePresenter';
import { GAME_CONFIG, type GameConfig } from '../config';
import type { RenderQuality } from '../config/rendering.config';
import { DebugController } from '../debug/DebugController';
import { DifficultyManager } from '../difficulty/DifficultyManager';
import { BusManager } from '../bus/BusManager';
import { BusPresenter } from '../bus/BusPresenter';
import { BusTrafficPresenter } from '../bus/traffic/BusTrafficPresenter';
import { DozerManager } from '../dozer/DozerManager';
import { DozerPhase } from '../dozer/DozerRide';
import { DozerPresenter } from '../dozer/DozerPresenter';
import { CollapsePresenter } from '../dynamic-events/CollapsePresenter';
import { DynamicEventManager } from '../dynamic-events/DynamicEventManager';
import { EffectsManager } from '../effects/EffectsManager';
import { HazardManager } from '../hazards/HazardManager';
import { HazardPresenter } from '../hazards/HazardPresenter';
import { InputAction, isGameplayAction } from '../input/InputAction';
import { InputManager } from '../input/InputManager';
import { KeyboardInputSource } from '../input/KeyboardInputSource';
import { TouchInputSource } from '../input/TouchInputSource';
import { LevelGenerator, tierHazardScale, tierPlainScale, tierSpecialScale } from '../level/generation/LevelGenerator';
import { DEFAULT_SEGMENTS, SegmentCatalog } from '../level/segments/SegmentCatalog';
import type { EnclosureDefinition, SegmentDefinition, SegmentGenerationRules } from '../level/segments/SegmentDefinition';
import { DEFAULT_DECORATIONS } from '../level/spawning/DecorationCatalog';
import { SegmentPopulator } from '../level/spawning/SegmentPopulator';
import type { TerrainQuery } from '../level/terrain/TerrainQuery';
import { withWalkableTops } from '../level/terrain/walkableTops';
import { TrackManager, trackFrame, type TrackFrame } from '../level/TrackManager';
import { TrackPresenter } from '../level/TrackPresenter';
import { TurnController, type TurnHint } from '../level/TurnController';
import { SequenceValidator } from '../level/validation/SequenceValidator';
import { validateSegmentDefinition } from '../level/validation/SegmentDefinitionValidator';
import { DEFAULT_OBSTACLES, ObstacleCatalog } from '../obstacles/ObstacleCatalog';
import { ObstacleManager } from '../obstacles/ObstacleManager';
import { ObstaclePresenter } from '../obstacles/ObstaclePresenter';
import { CollisionSystem } from '../physics/CollisionSystem';
import { PlayerController, type PlayerSnapshot } from '../player/PlayerController';
import { PlayerView } from '../player/PlayerView';
import { PursuitModel } from '../pursuers/PursuitModel';
import { GreyboxMaterials } from '../rendering/GreyboxMaterials';
import { GreyboxFactory } from '../rendering/greybox/GreyboxFactory';
import { MaterialFlasher } from '../rendering/MaterialFlasher';
import { CoveredSceneThrottle } from '../rendering/CoveredSceneThrottle';
import { RenderingEnvironment } from '../rendering/RenderingEnvironment';
import { ZoneLighting } from '../rendering/ZoneLighting';
import { CharacterLight } from '../rendering/CharacterLight';
import { createScene } from '../rendering/SceneSetup';
import { VisualFactory } from '../rendering/VisualFactory';
import { VisualPool } from '../rendering/VisualPool';
import { ScoreManager } from '../scoring/ScoreManager';
import { LocalProgressStore, nextSoundMode, SoundMode } from '../services/ProgressStore';
import { GAME_STATE_TRANSITIONS, GameState } from '../state/GameState';
import { Hud, type BusDashboard } from '../ui/Hud';
import { Screens } from '../ui/Screens';
import { lerp } from '../utils/math';
import { Random } from '../utils/Random';
import { StateMachine } from '../utils/StateMachine';
import { enclosureFactor } from '../zones/lightingBlend';
import { LightingZone, ZONE_SEED_STREAM, ZoneSchedule } from '../zones/ZoneSchedule';
import { EventBus } from './EventBus';
import { FixedStepLoop } from './FixedStepLoop';
import type { GameEvents } from './GameEvents';
import type { ObstacleInstance } from '../obstacles/ObstacleInstance';
import { QuizOverlay } from '../ui/QuizOverlay';
import { QuestionDeck, parseQuizPool, shuffleAnswers } from '../quiz/quizPool';
import { QuizPhase, QuizSession, roundAt } from '../quiz/QuizSession';
import { MiniGameKind } from '../minigames/MiniGame';
import { ChallengeBag, MiniGameSession, type GoldenChallenge } from '../minigames/MiniGameSession';
import { MiniGameOverlay } from '../ui/MiniGameOverlay';
import { DrinkBreak } from './DrinkBreak';
import { RunDirector } from './RunDirector';

export interface GameOptions {
  readonly canvas: HTMLCanvasElement;
  readonly uiRoot: HTMLElement;
  readonly config?: GameConfig;
  /** Fixed seed for reproducible tracks (debugging / tests). */
  readonly seed?: number;
  /** Per-segment generation overrides, e.g. bring a set piece forward for testing (?collapse=early). */
  readonly generationOverrides?: Readonly<Record<string, Partial<SegmentGenerationRules>>>;
  /** Creates the debug tooling (dev builds, or ?debug=1). */
  readonly debugAvailable?: boolean;
  /** Debug overlay visible right from the start. */
  readonly debugStartEnabled?: boolean;
  /** Rendering quality profile (shadow resolution, anti-aliasing, sky resolution). Default: high. */
  readonly quality?: RenderQuality;
  /** Fixed lighting zone instead of the day/night schedule (testing aid: ?zone=night). */
  readonly zoneOverride?: LightingZone;
  /** Preselected character id (testing aid: ?character=electrician) – otherwise the stored choice. */
  readonly character?: string;
}

/**
 * Composition root: creates and wires all systems, owns the main loop and
 * the game state machine. Contains no gameplay rules itself.
 */
export class Game {
  readonly events = new EventBus<GameEvents>();
  readonly config: GameConfig;
  readonly engine: Engine;
  readonly scene: Scene;
  readonly state = new StateMachine<GameState>(GameState.Boot, GAME_STATE_TRANSITIONS);

  // Gameplay (pure logic)
  readonly difficulty: DifficultyManager;
  readonly track: TrackManager;
  readonly turns: TurnController;
  readonly obstacles: ObstacleManager;
  /** What the runner stands on: the track's terrain plus walkable obstacle tops (container roofs). */
  readonly runnerTerrain: TerrainQuery;
  readonly collectibles: CollectibleManager;
  readonly dynamicEvents: DynamicEventManager;
  readonly hazards: HazardManager;
  /** Dozer set piece (SEG_DOZER_60M): jump on, push the earth into the pit. */
  readonly dozers: DozerManager;
  /** ructec-Bulli (SEG_BUS_*): 1500 m drive from about 1000 m on. */
  readonly buses: BusManager;
  readonly player: PlayerController;
  readonly score: ScoreManager;
  readonly pursuit: PursuitModel;
  readonly director: RunDirector;
  /** Bottle magnet: the character's permanent magnet and the magnet power-up. */
  readonly magnet: CollectibleMagnet;
  /** Beer-crate turbo: invulnerable, blinking, faster for a few seconds. */
  readonly turbo: TurboBoost;
  /** Extra life: the runner stops for a drink while the world waits. */
  private readonly drinkBreak: DrinkBreak;
  /** Bau-Quiz at the golden bottle: the picture freezes for a question, a correct answer banks the bonus. */
  private readonly quiz: QuizSession;
  private readonly quizOverlay: QuizOverlay;
  private quizQuestions: QuestionDeck | null = null;
  private readonly quizRng = new Random(Random.randomSeed());
  /** The answer of the current question has been credited / announced. */
  private quizResolved = false;
  /** Mini games at the golden bottle (crane, hammer & nail, concrete mixer), drawn at random with the quiz. */
  private readonly miniGame: MiniGameSession;
  private readonly miniGameOverlay: MiniGameOverlay;
  private miniGameResolved = false;
  /** Shuffle bag: every golden challenge once before any comes again, random start per run. */
  private readonly challenges = new ChallengeBag(() => this.quizRng.next());
  /** Scratch list for the free-stretch check of the drink. */
  private readonly aheadScratch: ObstacleInstance[] = [];

  // Presentation
  readonly assets: AssetRegistry;
  readonly loader: AssetLoader;
  readonly pool: VisualPool;
  readonly camera: ThirdPersonCamera;
  readonly audio: AudioManager;
  readonly ads: AdSlotBinder;
  private readonly adProvider: ManifestAdProvider;
  /** Counted impressions of booked campaigns → ad backend (only on hosts that have one, see backendByHost). */
  private readonly adReporter: AdReporter;
  private readonly adBackend: string | null;
  /** Server-side leaderboard of signed-in company users (ructec 2026-10-08, see leaderboard.config). */
  private readonly leaderboard: LeaderboardService;
  readonly trackView: TrackPresenter;
  readonly debug: DebugController | null = null;
  readonly environment: RenderingEnvironment;
  /** Day/night zones and enclosed-stretch lighting (Phase 3). */
  readonly lighting: ZoneLighting;
  /** The runner's own key light – stands out from the dark road on phones. */
  private readonly characterLight: CharacterLight;
  private zoneSchedule: ZoneSchedule;
  /** Zone of the previous frame during a run (null: not yet known – no announcement at the start). */
  private lastNight: boolean | null = null;
  private readonly zoneOverride: LightingZone | undefined;
  private readonly runnerWorld: TrackFrame = trackFrame();
  private readonly visuals: VisualFactory;
  private readonly adSurfaces: AdSurfacePresenter;
  private readonly collapses: CollapsePresenter;
  private readonly hazardView: HazardPresenter;
  private readonly dozerView: DozerPresenter;
  private readonly busView: BusPresenter;
  private readonly trafficView: BusTrafficPresenter;
  private readonly collectiblePresenter: CollectiblePresenter;
  /** Halo, glint and sparkles of the golden bottle (SEG_GOLDEN_RAMP_60M). */
  private readonly goldenShine: GoldenShinePresenter;
  private readonly flashers: MaterialFlasher;
  private readonly playerView: PlayerView;
  private readonly characters = new CharacterCatalog(CHARACTERS);
  /** Character of the current / next run – switchable on the start and game-over screen only. */
  private character: CharacterDefinition;
  private readonly hud: Hud;
  private readonly screens: Screens;
  /** Renders the 3D scene only now and then while the opaque loading / start screen hides it. */
  private readonly sceneThrottle: CoveredSceneThrottle;

  private readonly input = new InputManager();
  private readonly loop: FixedStepLoop;
  private readonly progress = new LocalProgressStore();
  private readonly fixedSeed: number | undefined;
  private readonly renderSnapshot: PlayerSnapshot = { distance: 0, x: 0, y: 0, colliderHeight: 0, lateralVelocity: 0 };
  private readonly renderFrame: TrackFrame = trackFrame();
  /** The runner's height along the track behind it – which walkable tops are ground for the camera. */
  private readonly runnerTrail: RunnerHeightTrail;
  private disposed = false;
  private simTime = 0;
  private renderTime = 0;

  constructor(options: GameOptions) {
    const config = (this.config = options.config ?? GAME_CONFIG);
    this.fixedSeed = options.seed;
    this.validateContent(config);

    // ── Engine & scene ──
    const quality = config.rendering.quality[options.quality ?? 'high'];
    this.engine = new Engine(
      options.canvas,
      config.rendering.antialias,
      { stencil: false, powerPreference: 'high-performance', limitDeviceRatio: quality.maxDevicePixelRatio },
      true,
    );
    const sceneBundle = createScene(this.engine, config.rendering);
    this.scene = sceneBundle.scene;
    this.environment = new RenderingEnvironment(this.scene, sceneBundle.sun, sceneBundle.ambient, config.rendering, quality);
    this.loop = new FixedStepLoop(config.rendering.fixedTimeStep, config.rendering.maxFrameTime);

    // ── Gameplay ──
    const events = this.events;
    this.difficulty = new DifficultyManager(config.difficulty);
    const obstacleCatalog = new ObstacleCatalog(DEFAULT_OBSTACLES);
    const collectibleCatalog = new CollectibleCatalog(DEFAULT_COLLECTIBLES);
    this.player = new PlayerController(config.player, config.world, events);
    this.score = new ScoreManager(config.scoring);
    const segmentCatalog = new SegmentCatalog(applyOverrides(DEFAULT_SEGMENTS, options.generationOverrides));
    const populator = new SegmentPopulator({
      obstacles: obstacleCatalog,
      collectibles: collectibleCatalog,
      decorations: DEFAULT_DECORATIONS,
      difficulty: this.difficulty,
      validator: new SequenceValidator({
        laneChangeDuration: config.player.laneChangeDuration,
        reactionTime: config.difficulty.reactionTime,
        maxAirTime: this.player.jumpPhysics.maxAirTime,
        speedAt: (distance) => this.difficulty.speedAt(distance),
      }),
      level: config.level,
      // SEG_22: more light masts and work lights in night zones (cosmetic stream only)
      decorationWeight: (assetId, distance) =>
        config.zones.enabled && this.zoneAt(distance) === LightingZone.Night ? (config.zones.nightDecorationWeights[assetId] ?? 1) : 1,
    });
    this.zoneSchedule = new ZoneSchedule(Random.deriveSeed(0, ZONE_SEED_STREAM), config.zones.schedule);
    this.zoneOverride = options.zoneOverride;
    this.track = new TrackManager({
      catalog: segmentCatalog,
      generator: new LevelGenerator(segmentCatalog, config.level.junctions, config.level.setPieces, tierHazardScale(this.difficulty), tierPlainScale(this.difficulty), tierSpecialScale(this.difficulty)),
      populator,
      events,
      level: config.level,
      world: config.world,
    });
    this.turns = new TurnController(this.track, config.level.junctions, events);
    this.obstacles = new ObstacleManager(obstacleCatalog, events);
    // After the ObstacleManager: a dozer module's obstacles exist when the DozerManager sees the segment spawn.
    this.dozers = new DozerManager({
      player: this.player,
      obstacles: this.obstacles,
      score: this.score,
      events,
      config: config.dozer,
      playerConfig: config.player,
      world: config.world,
      speedAt: (distance) => this.difficulty.speedAt(distance),
      fixedStep: config.rendering.fixedTimeStep,
      // Invulnerable after a hit, in the turbo (or god mode): onto the machine from the ground, never through it.
      isProtected: () => this.director.isProtected,
    });
    this.runnerTerrain = withWalkableTops(this.track, this.obstacles);
    this.runnerTrail = new RunnerHeightTrail(config.camera.runnerTrail.length, config.camera.runnerTrail.spacing);
    this.collectibles = new CollectibleManager(collectibleCatalog, config.collectibles, events);
    this.dynamicEvents = new DynamicEventManager(events);
    this.hazards = new HazardManager(config.hazards, config.world, events);
    // ructec-Bulli: after the ObstacleManager (clears the obstacles of road modules inside its ride on spawn).
    this.buses = new BusManager({
      player: this.player,
      obstacles: this.obstacles,
      collectibles: this.collectibles,
      score: this.score,
      events,
      config: config.bus,
      world: config.world,
      groundHeightAt: (distance, lateral) => this.track.groundHeightAt(distance, lateral),
      roadEnd: () => this.track.segments[this.track.segments.length - 1]?.endDistance ?? Number.POSITIVE_INFINITY,
      seed: () => this.track.seed,
      maxExtraLives: config.quiz.maxExtraLives,
    });
    this.pursuit = new PursuitModel(config.pursuit);
    this.magnet = new CollectibleMagnet(config.collectibles.magnet);
    this.turbo = new TurboBoost(config.collectibles.turbo);
    this.drinkBreak = new DrinkBreak(config.character.drinkBreak);
    this.quiz = new QuizSession(config.quiz);
    this.quizOverlay = new QuizOverlay(options.uiRoot, config.quiz, (index) => this.quiz.answer(index));
    void this.loadQuizQuestions();
    this.miniGame = new MiniGameSession(config.minigames);
    this.miniGameOverlay = new MiniGameOverlay(options.uiRoot, config.minigames, () => this.miniGame.tap(), config.quiz.wrongPenalty, config.rendering.environment.envUrl);
    this.character = this.characters.get(resolveCharacterId(options.character ?? this.progress.getCharacter(), config.character));
    this.director = new RunDirector({
      player: this.player,
      collisions: new CollisionSystem(this.obstacles, this.collectibles, this.hazards),
      obstacles: this.obstacles,
      collectibles: this.collectibles,
      score: this.score,
      pursuit: this.pursuit,
      turns: this.turns,
      events,
      config: config.player,
      world: config.world,
      rescue: this.track,
      hazards: this.hazards,
      magnet: this.magnet,
      turbo: this.turbo,
    });

    // ── Presentation ──
    this.assets = new AssetRegistry(ASSET_MANIFEST);
    this.loader = new AssetLoader(this.scene, this.assets);
    this.visuals = new VisualFactory(this.scene, this.assets, new GreyboxFactory(this.scene, new GreyboxMaterials(this.scene)), this.loader);
    this.pool = new VisualPool(this.visuals);
    this.flashers = new MaterialFlasher(this.loader, config.rendering.flashingMaterials);
    this.trackView = new TrackPresenter(this.scene, this.pool, events, config.level);
    const obstacleView = new ObstaclePresenter(this.pool, this.trackView, events);
    // Magnet pickups fly to the runner (PlayerView renders before the collectibles each frame).
    this.collectiblePresenter = new CollectiblePresenter(this.pool, this.trackView, config.collectibles, events, () => this.playerView.root.position);
    this.goldenShine = new GoldenShinePresenter(this.scene, this.trackView, config.effects.goldenShine, events);
    this.playerView = new PlayerView(this.scene, this.pool, this.character, config.rendering.playerView, config.character, config.effects.dust, config.dozer.presentation.rider);
    const heap = obstacleCatalog.get(config.dozer.heapObstacleId).colliders[0];
    this.dozerView = new DozerPresenter(
      this.scene,
      this.pool,
      this.trackView,
      obstacleView,
      this.dozers,
      config.dozer,
      config.world,
      heap?.size ?? { x: 1, y: 1, z: 1 },
      config.rendering.fixedTimeStep,
      events,
    );
    this.busView = new BusPresenter(this.pool, this.track, this.buses, config.bus, config.rendering.fixedTimeStep, () => this.player.current.distance, events);
    this.trafficView = new BusTrafficPresenter(this.pool, this.track, this.buses.traffic, config.bus, config.rendering.fixedTimeStep);
    this.bindCharacterReactions();
    this.camera = new ThirdPersonCamera(this.scene, config.camera);
    this.environment.attachCamera(this.camera.camera);
    this.lighting = new ZoneLighting(this.scene, sceneBundle.sun, this.environment, config.rendering, config.zones);
    this.characterLight = new CharacterLight(this.scene, config.rendering.characterLight, quality.characterLight);
    this.audio = new AudioManager(events, AUDIO_CONFIG, new SynthSoundBackend(AUDIO_CONFIG, this.progress.getMusicEnabled(), this.progress.getSoundMode() !== SoundMode.Mute));
    this.collapses = new CollapsePresenter(this.obstacles, obstacleView, this.camera, config.events.collapse, events);
    this.hazardView = new HazardPresenter(this.scene, this.pool, this.trackView, config.hazards, config.world, events);
    this.adProvider = new ManifestAdProvider(HOUSE_CAMPAIGNS, adImageOrigins(config.advertising));
    this.adBackend = adBackendUrl(config.advertising, globalThis.location?.hostname ?? '');
    this.adReporter = new AdReporter(this.adBackend);
    events.on('ad:impression', ({ impression }) => this.adReporter.record(impression.campaignId));
    // booking per game (ructec 2026-10-05): one report per run with every booking seen in it
    events.on('run:started', () => this.adReporter.startRun());
    events.on('run:ended', () => {
      this.adReporter.endRun();
      this.refreshAds();
    });
    // the TrackPresenter places the decorations first (it subscribed earlier) – their ad surfaces (roadside scaffold banner) join
    this.ads = new AdSlotBinder(this.adProvider, config.advertising, events, () => this.player.current.distance, (segment) => this.trackView.decorationAdSlots(segment));
    this.adSurfaces = new AdSurfacePresenter(this.scene, this.ads, this.trackView, this.camera.camera, config.advertising);

    // leaderboard: run id at the start, one score report at the end (signed-in players only)
    this.leaderboard = new LeaderboardService(leaderboardBackendUrl(LEADERBOARD_CONFIG, globalThis.location?.hostname ?? ''));
    events.on('run:started', () => this.leaderboard.startRun());

    // ── UI ──
    this.hud = new Hud(options.uiRoot, config.scoring, config.effects, () => this.togglePause(), () => this.toggleMusic());
    // "Anmelden" leads to the portal's /spiel-anmelden, which sends the player back here with a Spielausweis
    const ui = globalThis.location
      ? { ...config.ui, account: { ...config.ui.account, loginUrl: leaderboardLoginUrl(LEADERBOARD_CONFIG, globalThis.location) } }
      : config.ui;
    this.screens = new Screens(options.uiRoot, {
      onStart: () => this.startRun(),
      onResume: () => this.resume(),
      onRestart: () => this.restart(),
      onHome: () => this.returnToStart(),
      onToggleMusic: () => this.toggleMusic(),
      onSelectCharacter: (id) => this.selectCharacter(id),
      onStepCharacter: (direction) => this.selectCharacter(stepCharacter(this.character.id, direction, config.character)),
      onSignOut: () => {
        this.leaderboard.signOut();
        this.refreshLeaderboard();
      },
    }, characterChoices(this.characters, config.character), ui, this.character.id);
    this.screens.setSelectedCharacter(this.character.id);
    this.screens.setBestScore(this.progress.getBestScore());
    this.screens.setLeaderboardSignedIn(this.leaderboard.signedIn, null);
    // signed in: ask the server who we are (short name, company, own place) – then the list with the own row
    void this.leaderboard.whoAmI().then(() => this.refreshLeaderboard());
    this.screens.setSoundMode(this.progress.getSoundMode());
    this.hud.setSoundMode(this.progress.getSoundMode());
    this.sceneThrottle = new CoveredSceneThrottle(config.ui.coveredRendering);
    new EffectsManager(events, this.camera, this.hud, config.effects, config.player, {
      terrain: config.events.terrain,
      hazards: config.hazards.presentation,
      playerDistance: () => this.player.current.distance,
      dozer: config.dozer.presentation,
      bus: config.bus.presentation,
    });
    // After the effects' own notices: the quiz panel takes over from the golden-bottle toast (startQuiz clears it).
    events.on('collectible:golden', () => this.startGoldenChallenge());
    if (options.debugAvailable) this.debug = new DebugController(this, options.uiRoot, options.debugStartEnabled ?? config.debug.startEnabled);

    // ── Input ──
    this.input.add(new KeyboardInputSource(config.input.keyBindings));
    this.input.add(new TouchInputSource(window, config.input.swipe));

    this.state.onChange((from, to) => events.emit('game:stateChanged', { from, to }));
    this.state.onChange((_, to) => {
      this.quizOverlay.setSuspended(to === GameState.Paused);
      this.miniGameOverlay.setSuspended(to === GameState.Paused);
    });
    window.addEventListener('resize', this.onResize);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  private adsLoadedAt = 0;

  /**
   * Reloads the booked campaigns and a fresh session token after a run (at most every 2 min): a booking that ended or
   * moved up from the reservations shows in the next run, and the token (valid 3 h) never runs out in an open tab
   * (Befund Codex 2026-10-05). A failed reload keeps the last token – the house ads show until the next try.
   */
  private refreshAds(): void {
    if (!this.adBackend || this.disposed || performance.now() - this.adsLoadedAt < 120_000) return;
    this.adsLoadedAt = performance.now();
    void this.adProvider.load(this.adBackend, this.config.advertising.manifestTimeoutMs).then((errors) => {
      for (const error of errors) console.warn(`[ads] ${error}`);
      if (this.adProvider.session) this.adReporter.setSession(this.adProvider.session);
      // a run already going on keeps its sponsor to the end
      if (!this.state.is(GameState.Running, GameState.Paused)) this.showSponsor();
    });
  }

  /**
   * "sponsored by" (ructec 2026-10-05): the company this player's game belongs to (drawn by the provider when the game
   * loads, kept while its booking runs) – on the start screen and in the HUD the whole time. Nobody booked: no badge.
   */
  private showSponsor(): void {
    const sponsor = this.adProvider.sponsor;
    this.screens.setSponsor(sponsor);
    this.hud.setSponsor(sponsor);
  }

  /** Loads assets, prepares the first track and starts the render loop. */
  async init(): Promise<void> {
    this.state.transition(GameState.Loading);
    this.hud.setVisible(false);
    this.screens.showLoading();
    this.engine.runRenderLoop(this.onFrame);

    const { manifestTimeoutMs } = this.config.advertising;
    // a new player: the backend assigns the next company in turn (?neu=1) – later reloads keep it
    const manifestUrl = this.adBackend ? `${this.adBackend}?neu=1` : this.config.advertising.manifestUrl;
    const [errors, adErrors] = await Promise.all([
      this.loader.preload(['critical', 'run'], (loaded, total) => this.screens.setLoadingProgress(total > 0 ? loaded / total : 1)),
      this.adProvider.load(manifestUrl, manifestTimeoutMs),
    ]);
    if (this.disposed) return;
    for (const error of errors) console.error(`[assets] ${error}`);
    for (const error of adErrors) console.warn(`[ads] ${error}`);
    this.adReporter.setSession(this.adProvider.session);
    this.adsLoadedAt = performance.now();
    this.showSponsor();
    this.visuals.prepareGreyboxes();

    this.prepareRun();
    this.state.transition(GameState.Ready);
    this.screens.showStart();
    this.sceneThrottle.wake(); // the prepared track and character warm up their shaders behind the start screen
  }

  // ── Flow ─────────────────────────────────────────────────────────────────

  startRun(): void {
    if (this.state.is(GameState.Ready)) {
      this.state.transition(GameState.Running);
      this.screens.hideAll();
      this.hud.setVisible(true);
      this.events.emit('run:started', { seed: this.track.seed });
    }
  }

  restart(): void {
    if (!this.state.is(GameState.Running, GameState.Paused, GameState.GameOver)) return;
    this.prepareRun();
    // RUNNING/PAUSED go through READY; GAME_OVER may start the next run directly.
    if (this.state.is(GameState.Running, GameState.Paused)) this.state.transition(GameState.Ready);
    this.state.transition(GameState.Running);
    this.screens.hideAll();
    this.hud.setVisible(true);
    this.events.emit('run:started', { seed: this.track.seed });
  }

  /** Id of the character for the current / next run. */
  get characterId(): string {
    return this.character.id;
  }

  /**
   * Switches the character – only on the start and game-over screen, a run keeps its character.
   * The choice is remembered; the 3D character behind the screen changes along.
   */
  selectCharacter(id: string): void {
    const selectable = this.config.character.selectable.includes(id) && this.characters.has(id);
    if (!this.state.is(GameState.Ready, GameState.GameOver) || id === this.character.id || !selectable) return;
    this.character = this.characters.get(id);
    this.progress.setCharacter(id);
    this.playerView.setCharacter(this.character);
    this.screens.setSelectedCharacter(id);
    this.sceneThrottle.wake();
    // READY: the prepared run starts with the new perks. GAME_OVER: the next run prepares itself.
    if (this.state.is(GameState.Ready)) this.applyCharacterPerks();
  }

  togglePause(): void {
    // The quiz clock is part of the challenge: the HUD pause button may not stop it (hiding the tab still pauses).
    if (this.state.is(GameState.Running) && !this.goldenActive) this.pause();
    else if (this.state.is(GameState.Paused)) this.resume();
  }

  pause(): void {
    if (!this.state.is(GameState.Running)) return;
    this.player.releaseHolds();
    this.state.transition(GameState.Paused);
    this.screens.showPause();
  }

  resume(): void {
    if (!this.state.is(GameState.Paused)) return;
    this.state.transition(GameState.Running);
    this.screens.hideAll();
  }

  /**
   * Pause / game over → start screen: the run is abandoned (no score is submitted for an aborted run) and a fresh
   * track waits behind the start screen, as after loading.
   */
  returnToStart(): void {
    if (!this.state.is(GameState.Paused, GameState.GameOver)) return;
    this.prepareRun();
    this.state.transition(GameState.Ready);
    this.hud.setVisible(false);
    this.screens.setBestScore(this.progress.getBestScore());
    this.screens.showStart();
    this.sceneThrottle.wake();
  }

  /**
   * The sound switch, one step on: music and game sounds → game sounds only → silence → … (remembered on this device);
   * called from the toggle's click – a user gesture.
   */
  toggleMusic(): void {
    const mode = nextSoundMode(this.progress.getSoundMode());
    this.progress.setSoundMode(mode);
    this.audio.setMusicEnabled(mode === SoundMode.All);
    this.audio.setEffectsEnabled(mode !== SoundMode.Mute);
    this.screens.setSoundMode(mode);
    this.hud.setSoundMode(mode);
  }

  dispose(): void {
    this.disposed = true;
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.adReporter.dispose();
    this.input.dispose();
    this.engine.stopRenderLoop();
    this.loader.dispose();
    this.scene.dispose();
    this.engine.dispose();
    this.events.clear();
  }

  // ── Run lifecycle ────────────────────────────────────────────────────────

  private prepareRun(): void {
    const seed = this.fixedSeed ?? Random.randomSeed();
    this.director.reset();
    this.pursuit.reset();
    this.drinkBreak.reset();
    this.applyCharacterPerks();
    this.dynamicEvents.reset();
    this.hazards.reset();
    this.turns.reset();
    this.player.reset(0);
    this.playerView.reset();
    this.audio.reset();
    this.hud.clearToasts();
    this.quiz.reset();
    this.quizOverlay.hide();
    this.miniGame.reset();
    this.miniGameOverlay.hide();
    this.challenges.reset();
    this.setFrozen(false);
    this.zoneSchedule = new ZoneSchedule(Random.deriveSeed(seed >>> 0, ZONE_SEED_STREAM), this.config.zones.schedule);
    this.lighting.reset(this.zoneAt(0) === LightingZone.Night);
    this.lastNight = null;
    // Before the new track spawns its modules (their dozers are registered on spawn).
    this.dozers.reset();
    this.dozerView.reset();
    this.buses.reset();
    this.busView.reset();
    this.trafficView.reset();
    this.track.start(seed);
    this.runnerTrail.clear();
    this.loop.reset();
    this.simTime = 0;
    this.updateRenderState(1);
    this.camera.snap(this.cameraInput());
  }

  /** Applies the selected character's perks to score, run rules and magnet for a new run. */
  private applyCharacterPerks(): void {
    const perks = perksOf(this.character.id, this.config.character);
    this.score.reset(perks);
    this.director.applyPerks(perks);
    this.magnet.reset(perks);
  }

  private endRun(): void {
    const s = this.score.snapshot();
    const summary = { seed: this.track.seed, distance: s.distance, bottles: s.bottles, pickups: s.pickups, score: s.score, reason: this.director.reason };
    const isNewBest = this.progress.submitScore(s.score);
    this.screens.setBestScore(this.progress.getBestScore());
    this.state.transition(GameState.GameOver);
    this.events.emit('run:ended', summary);
    this.screens.showGameOver(summary, this.progress.getBestScore(), isNewBest);
    // signed in: the run is reported automatically – game over shows the place instead of "Anmelden"
    this.screens.setRunRanking(this.leaderboard.signedIn ? 'Wird in die Bestenliste eingetragen …' : null);
    void this.leaderboard.endRun(s.score, s.distance).then((result) => {
      this.screens.setRunRanking(result
        ? `${result.newBest ? 'Neuer Bestwert – ' : ''}Platz ${result.rank} in der Bestenliste (Bestwert ${formatNumber(result.score)})`
        : this.leaderboard.signedIn ? 'Lauf konnte nicht gewertet werden.' : null);
      this.refreshLeaderboard();
    });
  }

  /** Server leaderboard → start-screen preview and leaderboard page (never blocks the game). */
  private refreshLeaderboard(): void {
    void this.leaderboard.list(this.config.ui.leaderboard.listSize).then(({ entries, own }) => {
      this.screens.setLeaderboard(entries, own);
      this.screens.setLeaderboardSignedIn(this.leaderboard.signedIn, this.leaderboard.player);
      this.hud.setPlayer(this.leaderboard.signedIn ? this.leaderboard.player : null);
    });
  }

  // ── Loop ─────────────────────────────────────────────────────────────────

  private readonly onFrame = (): void => {
    const frameSeconds = Math.min(this.engine.getDeltaTime() / 1000, this.config.rendering.maxFrameTime);
    for (const action of this.input.drain()) this.handleAction(action);

    let alpha = 1;
    if (this.state.is(GameState.Running) && this.goldenActive) {
      // Bau-Quiz or mini game: the picture is frozen, only its clock and the bonus run (real seconds).
      if (this.quiz.active) this.updateQuiz(frameSeconds);
      else this.updateMiniGame(frameSeconds);
      this.loop.reset();
    } else if (this.state.is(GameState.Running)) {
      // Extra life: the runner stops for a drink while the world waits (simulation frozen, the clip plays on).
      const drinking = this.drinkBreak.active;
      const waiting = this.drinkBreak.update(frameSeconds, this.canDrink());
      for (const cue of this.drinkBreak.cues) this.events.emit('drink:cue', { cue });
      if (waiting) {
        this.loop.reset();
        // Swing round towards the middle of the road – never out over the fence.
        if (!drinking) this.camera.showcase.start(this.player.current.x > 0 ? 1 : -1, this.config.camera.drinkShowcase);
      } else {
        alpha = this.loop.advance(frameSeconds, this.fixedStep);
        if (this.director.isOver && this.state.is(GameState.Running)) this.endRun();
      }
    }
    if (!this.state.is(GameState.Paused) && !this.goldenActive) this.renderTime += frameSeconds;

    this.render(frameSeconds, alpha);
    this.trackView.flush();
    if (this.sceneThrottle.shouldRender(frameSeconds, this.screens.coversScene)) this.scene.render();
  };

  private readonly fixedStep = (dt: number): void => {
    this.simTime += dt;
    const distance = this.player.current.distance;
    // The turbo speeds up the runner on foot only – the dozer keeps its pace (its jumps are computed for it).
    const speed = this.difficulty.speedAt(distance) * (this.player.vehicle ? 1 : this.turbo.speedFactor);
    this.player.step(dt, {
      speed,
      time: this.simTime,
      groundHeightAt: (d, x) => this.track.groundHeightAt(d, x),
      terrain: this.runnerTerrain,
      tryTurn: (direction) => this.turns.tryDecide(direction, this.player.current.distance, this.player.current.x, speed),
      ceilingAt: (minZ, maxZ, minX, maxX) => {
        const minHeight = this.config.player.collider.slidingHeight;
        return lowest(this.obstacles.lowestCeiling(minZ, maxZ, minX, maxX, minHeight), this.hazards.lowestCeiling(minZ, maxZ, minX, maxX, minHeight));
      },
    });
    this.track.update(this.player.current.distance);
    // Moving hazards step before the collision check – the RunDirector sees this step's colliders.
    this.hazards.update(dt, this.player.current.distance, this.difficulty.speedAt(this.player.current.distance));
    // After the player moved, before the RunDirector's contacts: a jump onto the dozer mounts it (no hit), heaps
    // the blade meets are pushed – everything else the rider meets is the RunDirector's.
    this.dozers.step(dt);
    this.buses.step(dt);
    this.director.step(dt);
    this.dynamicEvents.update(dt, this.player.current.distance);
  };

  private render(dt: number, alpha: number): void {
    this.updateRenderState(alpha);
    const paused = this.state.is(GameState.Paused) || this.goldenActive;
    // The dozer first: the rider sits on its seat as drawn this frame.
    this.dozerView.render(alpha, dt, paused || this.drinkBreak.active);
    this.busView.render(alpha, dt, paused || this.drinkBreak.active);
    this.trafficView.render(alpha, dt, paused || this.drinkBreak.active);
    this.playerView.render(dt, {
      snapshot: this.renderSnapshot,
      frame: this.renderFrame,
      state: this.player.state,
      invulnerable: this.director.isInvulnerable || this.director.isTurbo,
      time: this.renderTime,
      running: this.state.is(GameState.Running, GameState.Paused, GameState.GameOver),
      paused: this.state.is(GameState.Paused) || this.goldenActive,
      speed: this.player.speed,
      normalizedSpeed: this.difficulty.normalizedSpeed(this.player.speed),
      bend: this.turns.bend(this.renderSnapshot.distance, this.renderSnapshot.x),
      drink: this.drinkBreak.progress,
      hitTrip: this.player.hitWhileAirborne,
      seat: this.dozerView.seatPose() ?? this.busView.seatPose(),
    });
    const turnHint = this.turns.hint(this.renderSnapshot.distance);
    this.camera.turnAnticipation.setUpcomingTurn(anticipationSign(turnHint), turnHint?.distanceToPivot ?? Infinity);
    this.camera.update(dt, this.cameraInput());
    this.environment.update(this.camera.camera);
    this.updateLighting(dt);
    const onGround = this.player.state === PlayerState.Running || this.player.state === PlayerState.Stumbling;
    this.audio.update(dt, this.renderSnapshot.distance, this.state.is(GameState.Running) && onGround);
    this.audio.updateEngine(this.state.is(GameState.Running) && !paused ? dt : 0, this.dozerEngine());
    this.collectiblePresenter.render(this.renderTime);
    this.goldenShine.render(this.renderTime);
    this.flashers.update(this.renderTime);
    this.hazardView.render(alpha, this.renderTime);
    if (this.state.is(GameState.Running)) this.collapses.update(dt);
    if (this.state.is(GameState.Running)) this.adSurfaces.update(dt);
    else this.ads.resetVisibility();
    this.debug?.render(dt);
    this.hud.update(this.state.is(GameState.Paused) || this.goldenActive ? 0 : dt, {
      score: this.score.snapshot(),
      pursuitPressure: this.pursuit.pressure,
      pursuitCritical: this.pursuit.isCritical,
      turbo: this.turbo.fraction,
      bus: this.busDashboard(),
    });
  }

  /**
   * The runner can stop for the extra-life drink: running on the ground (not jumping, sliding, stumbling),
   * settled in a lane, the run not over – and the stretch ahead is free, the run goes on from there.
   */
  private canDrink(): boolean {
    const p = this.player;
    if (p.state !== PlayerState.Running || this.director.isOver) return false;
    if (Math.abs(p.current.lateralVelocity) >= this.config.character.drinkBreak.settledLateralSpeed) return false;
    return this.stretchAheadIsFree();
  }

  /** No obstacle in the runner's lane, no moving hazard, no junction decision, no ground edge, nothing overhead. */
  private stretchAheadIsFree(): boolean {
    const c = this.config.character.drinkBreak;
    const { distance, x, y } = this.player.current;
    const to = distance + Math.max(c.minClearMeters, this.player.speed * c.clearAheadSeconds);
    const half = this.config.player.collider.width / 2 + c.laneMargin;
    if (this.turns.hint(distance) || this.turns.hint(to)) return false;
    if (this.hazards.query(distance, to).length > 0) return false;
    if (this.obstacles.query(distance, to, this.aheadScratch).some((o) => o.bounds.maxX > x - half && o.bounds.minX < x + half)) return false;
    if (this.track.lowestCeilingIn(distance, to, x - half, x + half, 0) !== null) return false;
    for (let i = 1; i <= c.groundSamples; i++) {
      const ground = this.track.groundHeightAt(distance + ((to - distance) * i) / c.groundSamples, x);
      if (ground === null || Math.abs(ground - y) > this.config.player.maxStepHeight) return false;
    }
    return true;
  }

  // ── Bau-Quiz ────────────────────────────────────────────────────────────

  /** The question pool (public JSON today, the backend later); a broken entry is skipped with a warning. */
  private async loadQuizQuestions(): Promise<void> {
    try {
      const response = await fetch(this.config.quiz.questionsUrl);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const pool = parseQuizPool(await response.json(), this.config.quiz);
      for (const error of pool.errors) console.warn(`[quiz] ${error}`);
      this.quizQuestions = new QuestionDeck(pool.questions, this.quizRng);
    } catch (error) {
      console.warn(`[quiz] question pool not loaded (${this.config.quiz.questionsUrl}):`, error);
    }
  }

  /** Quiz or a mini game holds the run at a golden bottle. */
  private get goldenActive(): boolean {
    return this.quiz.active || this.miniGame.active;
  }

  /**
   * Golden bottle (user 2026-10-04): the quiz and the mini games from a shuffle bag – each once before any comes
   * again, random start; the quiz only while its question pool is loaded.
   */
  private startGoldenChallenge(): void {
    if (this.goldenActive) return;
    const available: GoldenChallenge[] = this.config.minigames.enabled ? [MiniGameKind.Crane, MiniGameKind.Hammer, MiniGameKind.Mixer] : [];
    if (this.quizQuestions) available.push('QUIZ');
    const challenge = this.challenges.next(available);
    if (!challenge) return;
    if (challenge === 'QUIZ') this.startQuiz();
    else this.startMiniGame(challenge);
  }

  /** A mini game over the frozen picture: the quiz round of this distance sets its bonus and difficulty. */
  private startMiniGame(kind: MiniGameKind): void {
    const round = roundAt(this.player.current.distance, this.config.quiz);
    if (!round) return;
    const level = Math.max(0, this.config.quiz.rounds.indexOf(round));
    this.miniGame.start(kind, level, round.bonus, () => this.quizRng.next());
    this.miniGameResolved = false;
    this.hud.clearToasts();
    this.setFrozen(true);
    this.miniGameOverlay.update(this.miniGame, 0);
    this.events.emit('minigame:started', { kind });
  }

  private updateMiniGame(dt: number): void {
    const game = this.miniGame.game;
    // the clock waits until the game's 3D models are on screen (first use loads them)
    const ready = game ? this.miniGameOverlay.isReady(game.kind) : true;
    const done = this.miniGame.update(ready ? dt : 0, (attempt) => {
      if (game) this.events.emit('minigame:attempt', { kind: game.kind, grade: attempt.grade, gained: attempt.gained });
    });
    const outcome = this.miniGame.outcome;
    if (outcome && !this.miniGameResolved) {
      // credited as soon as the last attempt is judged – the counter shows it during the result
      this.miniGameResolved = true;
      if (outcome.bonus > 0) {
        const lives = this.score.addBonusBottles(outcome.bonus, this.config.quiz.maxExtraLives);
        if (lives > 0) this.events.emit('score:extraLife', { extraLives: this.score.snapshot().extraLives });
      } else {
        this.score.removeBottles(this.config.quiz.wrongPenalty);
      }
      this.events.emit('minigame:ended', outcome);
    }
    this.miniGameOverlay.update(this.miniGame, dt);
    if (done) this.setFrozen(false);
  }

  /** Golden bottle: freeze the picture and ask a random question (nothing happens without a pool). */
  private startQuiz(): void {
    const round = roundAt(this.player.current.distance, this.config.quiz);
    const question = round && !this.goldenActive ? this.quizQuestions?.next(round.difficulty) : null;
    if (!round || !question) return;
    this.quiz.start(shuffleAnswers(question, this.quizRng), round.bonus);
    this.quizResolved = false;
    // The panel is the golden bottle's notice now – a toast would only sit half hidden behind it.
    this.hud.clearToasts();
    this.setFrozen(true);
    this.quizOverlay.update(this.quiz);
    this.events.emit('quiz:started', { questionId: question.id });
  }

  private updateQuiz(dt: number): void {
    const before = Math.ceil(this.quiz.secondsLeft);
    const done = this.quiz.update(dt);
    const after = Math.ceil(this.quiz.secondsLeft);
    if (this.quiz.phase === QuizPhase.Asking && after < before) this.events.emit('quiz:tick', { secondsLeft: after });
    const outcome = this.quiz.outcome;
    if (outcome && !this.quizResolved) {
      // Answered (tap / key between frames) or timed out: credit the bonus right away – the counter shows it.
      this.quizResolved = true;
      if (outcome.correct && outcome.bonus > 0) {
        const lives = this.score.addBonusBottles(outcome.bonus, this.config.quiz.maxExtraLives);
        if (lives > 0) this.events.emit('score:extraLife', { extraLives: this.score.snapshot().extraLives });
      } else if (!outcome.correct) {
        // wrong or too late (user 2026-10-04): 100 bottles off
        this.score.removeBottles(this.config.quiz.wrongPenalty);
      }
      this.events.emit('quiz:answered', outcome);
    }
    this.quizOverlay.update(this.quiz);
    if (done) this.setFrozen(false);
  }

  /** Frozen picture: dimmed, desaturated canvas while the quiz is up. */
  private setFrozen(frozen: boolean): void {
    const canvas = this.engine.getRenderingCanvas();
    if (canvas) canvas.style.filter = frozen ? this.config.quiz.freezeFilter : '';
    this.hud.holdToasts(frozen);
  }


  /** Day/night zone at the runner and enclosed stretches around the camera → scene lighting. */
  private updateLighting(dt: number): void {
    const r = this.renderSnapshot;
    const { enclosure, inside } = this.enclosureAtCamera();
    // The runner light hangs over the centre line (site floodlight feel, no hot spots on side walls).
    this.track.trackToWorld(0, r.y, r.distance, this.runnerWorld);
    const night = this.zoneAt(r.distance) === LightingZone.Night;
    if (this.state.is(GameState.Running)) {
      if (this.lastNight !== null && night !== this.lastNight) this.events.emit('zone:changed', { night });
      this.lastNight = night;
    }
    this.lighting.update(dt, {
      night,
      enclosure: enclosure?.kind ?? null,
      enclosureFactor: inside,
      runner: this.runnerWorld,
    });
    this.characterLight.update(this.playerView.visualRoot, this.runnerWorld.yaw, this.lighting.daylight);
  }

  /** Day/night zone of a track distance (schedule of the run, or the ?zone= override). */
  zoneAt(distance: number): LightingZone {
    return this.zoneOverride ?? this.zoneSchedule.zoneAt(distance);
  }

  /** Gameplay events → one-shot character reactions (presentation only). */
  private bindCharacterReactions(): void {
    this.events.on('player:laneChanged', ({ from, to }) => this.playerView.onLaneChanged(from, to));
    this.events.on('player:stumbled', () => this.playerView.onStumbled(this.player.current.lateralVelocity));
    this.events.on('collectible:collected', ({ bottleValue }) => this.playerView.onCollected(this.renderTime, bottleValue));
    this.events.on('score:extraLife', () => this.drinkBreak.request());
    this.events.on('player:died', ({ reason }) => this.playerView.onDied(reason));
    this.events.on('player:landed', ({ impactSpeed }) => this.playerView.onLanded(impactSpeed));
  }

  private updateRenderState(alpha: number): void {
    const prev = this.player.previous;
    const curr = this.player.current;
    const r = this.renderSnapshot;
    r.distance = lerp(prev.distance, curr.distance, alpha);
    r.x = lerp(prev.x, curr.x, alpha);
    r.y = lerp(prev.y, curr.y, alpha);
    r.colliderHeight = curr.colliderHeight;
    r.lateralVelocity = curr.lateralVelocity;
    this.track.sampleFrame(r.distance, this.renderFrame);
    this.runnerTrail.record(r.distance, r.y);
  }

  /** ructec-Bulli dashboard (HUD) while the runner drives. */
  private busDashboard(): BusDashboard | null {
    const ride = this.buses.active;
    if (!ride) return null;
    const { rideLength, cargo } = this.config.bus;
    const driven = this.buses.vehicleSeat.driven;
    return {
      kmh: this.player.speed * 3.6,
      left: rideLength - driven,
      share: 1 - driven / rideLength,
      cargo: ride.cargo,
      maxCargo: cargo.pieces,
      rear: this.buses.rearWarnings(),
    };
  }

  /** The nearest dozer's engine (idling or driving), relative to the runner – the AudioManager chugs it. */
  private dozerEngine(): EngineSound | null {
    // the ructec-Bulli the runner drives: its engine revs up with the speed (≈ 30 → 50 beats/s)
    if (this.buses.active) return { ahead: 0, rate: 8 + this.player.speed * 2, range: 30, minLoudness: 1 };
    const engine = this.config.dozer.presentation.engine;
    let nearest: EngineSound | null = null;
    for (const ride of this.dozers.all()) {
      const ahead = ride.distance - this.renderSnapshot.distance;
      if (nearest && Math.abs(ahead) >= Math.abs(nearest.ahead)) continue;
      const driving = ride.phase === DozerPhase.Driving || ride.pose.speed > 0;
      nearest = { ahead, rate: driving ? engine.driveRate : engine.idleRate, range: engine.range, minLoudness: engine.minLoudness };
    }
    return nearest;
  }

  private cameraInput(): CameraFollowInput {
    return {
      snapshot: this.renderSnapshot,
      frame: this.renderFrame,
      normalizedSpeed: this.difficulty.normalizedSpeed(this.player.speed),
      time: this.renderTime,
      sliding: this.player.isSliding,
      driving: this.player.state === PlayerState.Driving,
      drivingBus: this.buses.active !== null,
      busSpeedShare: this.buses.active ? Math.min(1, this.player.speed / 22) : 0,
      enclosed: this.enclosureAtCamera().inside,
      standingLevel: this.player.standingLevel,
      ceilingAt: this.ceilingAt,
      groundAt: this.groundAt,
      bend: this.playerView.bend,
    };
  }

  /** Enclosed stretch (tunnel, interior, shell) at the camera and how far inside it is (0..1, faded at the ends). */
  private enclosureAtCamera(): { readonly enclosure: EnclosureDefinition | undefined; readonly inside: number } {
    const cameraDistance = this.renderSnapshot.distance - this.config.camera.followDistance;
    const segment = this.track.segmentAt(cameraDistance);
    const enclosure = segment?.definition.enclosure;
    const inside = segment ? enclosureFactor(enclosure, segment.localZ(cameraDistance), this.config.zones.enclosureFadeMeters) : 0;
    return { enclosure, inside };
  }

  /** Overhead obstacles, hanging loads and segment ceilings along the camera's path – keeps it from cutting through scaffolds. */
  private readonly ceilingAt = (fromDistance: number, toDistance: number, lateral: number): number | null => {
    const margin = 0.5;
    const minHeight = this.config.camera.overheadMinHeight;
    const obstacle = this.obstacles.lowestCeiling(fromDistance, toDistance, lateral - margin, lateral + margin, minHeight);
    const hazard = this.hazards.lowestCeiling(fromDistance, toDistance, lateral - margin, lateral + margin, minHeight);
    const terrain = this.track.lowestCeilingIn(fromDistance, toDistance, lateral - margin, lateral + margin, minHeight, 'all', this.cameraTopReach(fromDistance, toDistance));
    return lowest(lowest(obstacle, hazard), terrain);
  };

  /** Raised ground along the camera's path (container roofs, platforms) – the camera never dips into it. */
  private readonly groundAt = (fromDistance: number, toDistance: number, lateral: number): number | null => {
    const margin = 0.5;
    return this.runnerTerrain.highestGroundIn(fromDistance, toDistance, lateral - margin, lateral + margin, this.cameraTopReach(fromDistance, toDistance));
  };

  /**
   * Walkable tops the runner ran on along this stretch (pipe crowns, container roofs) are ground for the
   * camera, not overhead structure: it rises over them and stays above them after the runner dropped off
   * the end – it never glides down into a pipe the runner ran along. Where the runner ran through the
   * pipe, the crown stays a ceiling the camera follows it under.
   */
  private cameraTopReach(fromDistance: number, toDistance: number): number {
    return this.runnerTrail.topReachIn(fromDistance, toDistance, this.renderSnapshot.y, this.config.player.maxStepHeight);
  }

  private handleAction(action: InputAction): void {
    // The quiz overlay takes its own keys (A/B/C, 1/2/3); nothing else – not even pause – may stop the clock.
    // Paused by a hidden tab the panel is suspended and the pause screen's keys work again.
    if (this.goldenActive && !this.state.is(GameState.Paused)) return;
    // Leaderboard / sign-up page over the start screen: ESC goes back; no run start, no card switch behind it.
    if (this.state.is(GameState.Ready) && !this.screens.startVisible) {
      if (action === InputAction.Pause) this.screens.showStart();
      return;
    }
    // Start / game-over screen: left/right (arrow keys, A/D, horizontal swipe) switch the character card.
    const sideways = action === InputAction.MoveLeft ? -1 : action === InputAction.MoveRight ? 1 : 0;
    if (sideways !== 0 && this.state.is(GameState.Ready, GameState.GameOver)) {
      this.selectCharacter(stepCharacter(this.character.id, sideways, this.config.character));
      return;
    }
    switch (action) {
      case InputAction.Pause:
        this.togglePause();
        return;
      case InputAction.Confirm:
        if (this.state.is(GameState.Ready)) this.startRun();
        else if (this.state.is(GameState.GameOver)) this.restart();
        else if (this.state.is(GameState.Paused)) this.resume();
        return;
      case InputAction.Restart:
        this.restart();
        return;
      case InputAction.ToggleDebug:
        this.debug?.toggle();
        return;
      default:
        if (isGameplayAction(action) && this.state.is(GameState.Running) && !this.drinkBreak.active) this.player.enqueue(action, this.simTime);
    }
  }

  private readonly onResize = (): void => this.engine.resize();

  private readonly onVisibilityChange = (): void => {
    if (document.hidden) this.pause();
  };

  /**
   * Segment data is authored against WORLD_CONFIG – a custom world config
   * (lane width, …) must still match it, otherwise lanes and sockets diverge.
   */
  private validateContent(config: GameConfig): void {
    const errors = DEFAULT_SEGMENTS.flatMap((segment) => validateSegmentDefinition(segment, config.world));
    if (errors.length > 0) throw new Error(`Invalid segment definitions:\n${errors.join('\n')}`);
  }
}

/** Lower of two optional heights. */
function lowest(a: number | null, b: number | null): number | null {
  return a === null ? b : b === null ? a : Math.min(a, b);
}

/** Camera leans into the decided turn, or into the only way out of a forced turn. */
function anticipationSign(hint: TurnHint | null): -1 | 0 | 1 {
  if (!hint) return 0;
  const direction = hint.decided ?? (hint.forced && hint.options.length === 1 ? hint.options[0] : null);
  return direction === 'LEFT' ? -1 : direction === 'RIGHT' ? 1 : 0;
}

function applyOverrides(
  segments: readonly SegmentDefinition[],
  overrides: Readonly<Record<string, Partial<SegmentGenerationRules>>> | undefined,
): SegmentDefinition[] {
  return segments.map((definition) => {
    const override = overrides?.[definition.id];
    return override ? { ...definition, generation: { ...definition.generation, ...override } } : definition;
  });
}
